/**
 * ForecastAccessPanel — what a signed-out viewer sees on /forecast (05.3 D-16).
 *
 * WHAT THIS CHANGES AND WHAT IT DOES NOT.
 *
 * It changes what the denied state SAYS and where it points. It does NOT change
 * who is let in. The server-side Salesforce OAuth exec read gate (05.1 D-19) is
 * untouched: no bypass, no widened group, no server file in this plan's diff. A
 * viewer who follows the link and is still not authorised gets the same refusal
 * from the same gate — they just now know which door to try.
 *
 * WHY IT EXISTS: before this, a 401 on /api/forecast bounced immediately through
 * `apiFetch`'s redirect branch. Someone who simply was not signed in saw a flash
 * and a login screen with no explanation, and someone whose Salesforce session
 * had lapsed could loop. Nothing on screen ever said "sign in with Adeptia SSO",
 * which is the one fact that turns a dead end into a two-second fix.
 *
 * THE PANEL OFFERS A LINK; IT DOES NOT NAVIGATE. No `window.location.assign`
 * anywhere in this file, and an acceptance criterion asserts that. An
 * access-denied state that redirects on render is how the viewer lost the
 * message in the first place — the whole point is that they get to read it.
 */
import { Card } from '../ui/Card.js';

interface ForecastAccessPanelProps {
  /**
   * The sign-in target, resolved by the api-client for this path so the panel
   * and the automatic-redirect branch can never point at different identity
   * layers (Salesforce OAuth for /api/forecast, not Entra).
   */
  signInUrl: string;
}

export function ForecastAccessPanel({ signInUrl }: ForecastAccessPanelProps): React.JSX.Element {
  return (
    <Card data-testid="forecast-access-panel" className="!border-2 !border-brand-500">
      <p className="text-[0.72rem] font-semibold uppercase tracking-wider text-brand-700">
        Sign in to view the forecast
      </p>
      <p className="mt-2 text-sm text-ink-700">
        The Combined Revenue Forecast is available to Adeptia execs and sales managers. Sign in
        with your Adeptia Salesforce SSO to view it.
      </p>
      <p className="mt-3">
        <a
          href={signInUrl}
          data-testid="forecast-access-signin"
          className="inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          Sign in with Adeptia SSO
        </a>
      </p>
    </Card>
  );
}
